import { type GroupNameProblem, groupNameProblem, type StrongerMemoryGroup } from '@echo/core';
import { useMyGroup, useRenameGroup } from '@echo/core/react';
import { Button, Card, TextField } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CircleAlert } from 'lucide-react';
import { useState } from 'react';
import { ToolbarBack } from '@/components/empty-state-frame';
import { GroupActionFailed } from '@/components/group-load-state';
import { useGoBack } from '@/lib/use-go-back';

export const Route = createFileRoute('/_app/account_/groups_/$id_/name')({
  component: GroupNamePage,
});

/**
 * Manage group › Edit group name, for its leaders: the name as it is, to change. Saving with no
 * name asks for one, as New group does; saved, Manage group shows the new name.
 */
function GroupNamePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useGoBack(
    () => void navigate({ to: '/account/groups/$id', params: { id }, replace: true }),
  );
  const { group } = useMyGroup(id);
  return (
    <>
      <ToolbarBack
        render={({ className, children }) => (
          <button type="button" className={className} onClick={goBack}>
            {children}
          </button>
        )}
      />
      {group?.manages ? <GroupNameForm group={group} onSaved={goBack} /> : null}
    </>
  );
}

function GroupNameForm({ group, onSaved }: { group: StrongerMemoryGroup; onSaved: () => void }) {
  const { t } = useLingui();
  const renaming = useRenameGroup(group.actorUuid);
  const [name, setName] = useState(group.name);
  const [checked, setChecked] = useState(false);
  const problem = checked ? groupNameProblem(name) : undefined;
  const problemText: Record<GroupNameProblem, string> = {
    required: t`Group name is required.`,
    'too-short': t`Use at least 2 characters.`,
    'too-long': t`Use 50 characters or fewer.`,
  };
  return (
    <>
      <h1 className="text-display-sm font-bold">
        <Trans>Edit group name</Trans>
      </h1>
      <form
        noValidate
        className="flex flex-col gap-section-sm pt-content-md"
        onSubmit={(event) => {
          event.preventDefault();
          setChecked(true);
          if (groupNameProblem(name) || renaming.isPending) return;
          renaming.mutate(name, { onSuccess: onSaved });
        }}
      >
        <Card className="flex flex-col gap-content-sm rounded-xl">
          <TextField
            label={t`Group name`}
            hint={t`Everyone you invite will see this name.`}
            error={problem ? problemText[problem] : undefined}
            errorIcon={<CircleAlert size={16} aria-hidden />}
            value={name}
            onChange={(event) => {
              setName(event.currentTarget.value);
              setChecked(false);
            }}
          />
          {renaming.isError ? <GroupActionFailed /> : null}
        </Card>
        <div className="flex justify-end">
          <Button type="submit" loading={renaming.isPending}>
            {t`Save`}
          </Button>
        </div>
      </form>
    </>
  );
}
